import { CartErrorEvent, ThemeEvents } from '@theme/events';

/** Shared auto-dismiss duration for inline PDP/QV cart-add error messages (ms). */
export const CART_ADD_ERROR_DISPLAY_MS = 10000;
/** @type {Map<HTMLElement, number>} */
const inlineErrorTimeouts = new Map();

/**
 * @param {unknown} data
 * @param {Response} [response]
 * @returns {string}
 */
export function parseCartAddError(data, response) {
  const payload = /** @type {{ description?: string; message?: string; errors?: unknown }} */ (
    data || {}
  );

  if (typeof payload.description === 'string' && payload.description.trim()) {
    return payload.description.trim();
  }

  const message = typeof payload.message === 'string' ? payload.message.trim() : '';
  if (message && message.toLowerCase() !== 'cart error') {
    return message;
  }

  if (typeof payload.errors === 'string' && payload.errors.trim()) {
    return payload.errors.trim();
  }

  if (payload.errors && typeof payload.errors === 'object') {
    const values = Object.values(payload.errors).flat();
    const first = values.find((value) => typeof value === 'string' && value.trim());
    if (typeof first === 'string') {
      return first.trim();
    }
  }

  if (response?.status === 422) {
    return 'Unable to add that quantity to your bag.';
  }
  return 'Something went wrong. Please try again.';
}

/**
 * @param {HTMLElement | null | undefined} errorEl
 * @param {string} message
 */
export function showInlineCartError(errorEl, message) {
  if (!(errorEl instanceof HTMLElement) || !message) return;

  errorEl.classList.remove('hidden');

  const messageEl = errorEl.querySelector('.product-form-text__error-message');
  if (messageEl) {
    messageEl.textContent = message;
  } else {
    const textNode = errorEl.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorEl.appendChild(document.createTextNode(message));
    }
  }

  const existingTimeout = inlineErrorTimeouts.get(errorEl);
  if (existingTimeout) clearTimeout(existingTimeout);

  const timeoutId = window.setTimeout(() => {
    errorEl.classList.add('hidden');
    inlineErrorTimeouts.delete(errorEl);
  }, CART_ADD_ERROR_DISPLAY_MS);

  inlineErrorTimeouts.set(errorEl, timeoutId);
}

/**
 * @param {HTMLElement | null | undefined} errorEl
 */
export function clearInlineCartError(errorEl) {
  if (!(errorEl instanceof HTMLElement)) return;

  const existingTimeout = inlineErrorTimeouts.get(errorEl);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    inlineErrorTimeouts.delete(errorEl);
  }

  errorEl.classList.add('hidden');
}

/**
 * Hidden line-item property written by product-stack-hijack.js. Cart lines hold
 * stack variant ids; PDP/QV quantity selectors are keyed by the base variant.
 */
const STACK_BASE_VARIANT_PROPERTY = '_stack_base_variant';

/**
 * Units in the cart that count against a PDP/QV variant's quantity limit.
 * Ordinary lines match `variant_id`; Product Stack lines match via
 * `_stack_base_variant` (the merchandised base id the shopper selected).
 *
 * @param {{ items?: Array<{ variant_id?: number; quantity?: number; properties?: Record<string, unknown> }> } | null | undefined} cart
 * @param {string | number | null | undefined} variantId
 * @returns {number}
 */
export function cartQuantityForVariant(cart, variantId) {
  if (variantId == null || !cart?.items?.length) return 0;

  const wanted = String(variantId);
  let units = 0;

  for (const item of cart.items) {
    if (!item || typeof item !== 'object') continue;
    const quantity = Number(item.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) continue;

    if (String(item.variant_id) === wanted) {
      units += quantity;
      continue;
    }

    const baseRef = item.properties?.[STACK_BASE_VARIANT_PROPERTY];
    if (baseRef != null && String(baseRef) === wanted) {
      units += quantity;
    }
  }

  return units;
}

/**
 * Sync PDP quantity selector cart counts after a failed add that may have partially updated the cart.
 * @param {{ items?: Array<{ variant_id: number; quantity: number; properties?: Record<string, unknown> }> } | null | undefined} cart
 * @param {string | number | null | undefined} variantId
 */
export function syncQuantitySelectorsFromCart(cart, variantId) {
  if (!cart?.items || variantId == null) return;

  const variantIdStr = String(variantId);
  const cartQty = cartQuantityForVariant(cart, variantIdStr);

  document
    .querySelectorAll(`quantity-selector-component[data-variant-id="${variantIdStr}"]`)
    .forEach((selector) => {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) return;

      input.setAttribute('data-cart-quantity', String(cartQty));

      const component = /** @type {{ updateCartQuantity?: () => void }} */ (selector);
      if (typeof component.updateCartQuantity === 'function') {
        component.updateCartQuantity();
      }
    });
}

/**
 * Sync every cart line quantity to matching PDP/QV quantity selectors.
 * Also syncs base-variant selectors for Product Stack lines so the max-in-cart
 * guard sees family units rather than staying at 0.
 * @param {{ items?: Array<{ variant_id: number; quantity: number; properties?: Record<string, unknown> }> } | null | undefined} cart
 */
export function syncAllQuantitySelectorsFromCart(cart) {
  if (!cart?.items?.length) return;

  /** @type {Set<string>} */
  const variantIds = new Set();
  for (const item of cart.items) {
    if (item?.variant_id != null) variantIds.add(String(item.variant_id));
    const baseRef = item?.properties?.[STACK_BASE_VARIANT_PROPERTY];
    if (baseRef != null && baseRef !== '') variantIds.add(String(baseRef));
  }

  for (const variantId of variantIds) {
    syncQuantitySelectorsFromCart(cart, variantId);
  }
}

/**
 * @param {object} params
 * @param {unknown} params.errorData
 * @param {Response} [params.response]
 * @param {HTMLElement | null | undefined} [params.errorEl]
 * @param {string} [params.sourceId]
 * @param {string | number | null | undefined} [params.variantId]
 * @param {string | number | null | undefined} [params.productId]
 * @param {boolean} [params.skipInlineError]
 * @returns {Promise<string>}
 */
export async function handleCartAddFailure({
  errorData,
  response,
  errorEl,
  sourceId = '',
  variantId,
  productId,
  skipInlineError = false,
}) {
  const payload = /** @type {{ message?: string; description?: string; errors?: unknown }} */ (
    errorData || {}
  );
  const message = parseCartAddError(payload, response);

  if (!skipInlineError) {
    showInlineCartError(errorEl, message);
  }

  document.dispatchEvent(
    new CartErrorEvent(
      sourceId,
      payload.message || message,
      payload.description || message,
      payload.errors
    )
  );

  let cart = /** @type {{ item_count?: number; items?: Array<{ variant_id: number; quantity: number }> } | null} */ (
    null
  );

  try {
    const cartResponse = await fetch('/cart.js');
    if (cartResponse.ok) {
      cart = await cartResponse.json();
    }
  } catch {
    // fall through — still dispatch error event with flash message
  }

  if (cart) {
    syncAllQuantitySelectorsFromCart(cart);
  }

  if (typeof window !== 'undefined') {
    /** @type {any} */ (window).__bonobosPendingCartFlash = message;
  }

  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource: cart || {},
        sourceId,
        data: {
          didError: true,
          openDrawer: true,
          flashMessage: message,
          source: 'cart-add-error',
          ...(typeof cart?.item_count === 'number' ? { itemCount: cart.item_count } : {}),
          variantId: variantId != null ? String(variantId) : undefined,
          productId: productId != null ? String(productId) : undefined,
        },
      },
    })
  );

  return message;
}

/**
 * Announce a cart add that SUCCEEDED but not on the caller's terms — today only
 * a Product Stack partial allocation, where fewer units landed than were asked
 * for (see assets/product-stack-hijack.js).
 *
 * Same tail as handleCartAddFailure, minus the error semantics: re-read the cart
 * so the count is the truth rather than the caller's assumed delta, then let the
 * drawer show the message through its normal flash path. `itemCount` is
 * ABSOLUTE and `source` is deliberately not 'product-form-component', because
 * cart-icon.js treats that source as an increment — an absolute value overwrites
 * the caller's inflated count instead of compounding it.
 *
 * @param {object} params
 * @param {string} params.message
 * @param {string} [params.sourceId]
 * @param {string | number | null | undefined} [params.variantId]
 * @param {string | number | null | undefined} [params.productId]
 * @returns {Promise<string>}
 */
export async function announceCartAddNotice({ message, sourceId = '', variantId, productId }) {
  if (!message) return '';

  let cart = /** @type {{ item_count?: number; items?: Array<{ variant_id: number; quantity: number }> } | null} */ (
    null
  );

  try {
    const cartResponse = await fetch('/cart.js');
    if (cartResponse.ok) {
      cart = await cartResponse.json();
    }
  } catch {
    // fall through — still announce, just without a corrected count
  }

  if (cart) {
    syncAllQuantitySelectorsFromCart(cart);
  }

  if (typeof window !== 'undefined') {
    /** @type {any} */ (window).__bonobosPendingCartFlash = message;
  }

  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource: cart || {},
        sourceId,
        data: {
          didError: false,
          openDrawer: true,
          flashMessage: message,
          source: 'cart-add-notice',
          ...(typeof cart?.item_count === 'number' ? { itemCount: cart.item_count } : {}),
          variantId: variantId != null ? String(variantId) : undefined,
          productId: productId != null ? String(productId) : undefined,
        },
      },
    })
  );

  return message;
}

const api = {
  CART_ADD_ERROR_DISPLAY_MS,
  parseCartAddError,
  showInlineCartError,
  clearInlineCartError,
  cartQuantityForVariant,
  syncQuantitySelectorsFromCart,
  syncAllQuantitySelectorsFromCart,
  handleCartAddFailure,
  announceCartAddNotice,
};

if (typeof window !== 'undefined') {
  /** @type {any} */ (window).BonobosCartAddFeedback = api;
}

export default api;
